<template>
  <div class="chart-container">
    <div class="chart-header">
      <Dropdown
        :titleDropdown="selectedChart.name"
        position="right"
        colorArrow="black"
      >
        <ul>
          <li
            v-for="chart in availableCharts"
            :key="chart.type"
            @click="changeChartType(chart.type)"
            class="bkps-dropdown-item"
            :title="chart.description"
          >
            <i :class="chart.icon"></i>
            {{ chart.name }}
          </li>
        </ul>
      </Dropdown>

      <Dropdown
        :titleDropdown="selectedSort.name"
        position="right"
        colorArrow="black"
        class="ml-2"
      >
        <ul>
          <li
            v-for="sort in availableSorts"
            :key="sort.type"
            @click="changeSortType(sort.type)"
            class="bkps-dropdown-item"
            :title="sort.description"
          >
            <i :class="sort.icon"></i>
            {{ sort.name }}
          </li>
        </ul>
      </Dropdown>
    </div>
    <div ref="targetChart" style="height: 400px; width: 100%; margin-top: 10px;"></div>
  </div>
</template>

<script>
import Highcharts from 'highcharts/es5/highcharts'
import ChartMix from '@/components/charts/ChartMix.vue'
import ChartThemeMixin from '@/components/charts/ChartThemeMixin.vue'
import Dropdown from '@/components/shares/specialTag/Dropdown.vue'

export default {
  components: { Dropdown },
  props: {
    data: { required: true, type: Array },
    graphTitle: { required: true, type: String },
    loading: { required: true, type: Boolean },
  },
  data() {
    return {
      chartType: 'line',
      sortBy: 'value',
      chartInstance: null,
      availableCharts: [
        {
          type: 'line',
          name: 'Linha',
          icon: 'fas fa-chart-line',
          description: 'Gráfico de linha'
        },
        {
          type: 'area',
          name: 'Área',
          icon: 'fas fa-chart-area',
          description: 'Gráfico de área'
        },
        {
          type: 'column',
          name: 'Barras',
          icon: 'fas fa-chart-bar',
          description: 'Gráfico de barras'
        }
      ],
      availableSorts: [
        {
          type: 'value',
          name: 'Valor',
          icon: 'fas fa-dollar-sign',
          description: 'Ordenar por valor'
        },
        {
          type: 'count',
          name: 'Qtd',
          icon: 'fas fa-hashtag',
          description: 'Ordenar por quantidade'
        }
      ]
    }
  },
  computed: {
    selectedChart() {
      return this.availableCharts.find(chart => chart.type === this.chartType)
    },
    selectedSort() {
      return this.availableSorts.find(sort => sort.type === this.sortBy)
    }
  },
  methods: {
    changeChartType(type) {
      this.chartType = type
      this.createChart()
    },
    changeSortType(type) {
      this.sortBy = type
      this.createChart()
    },
    buildSeries() {
      if (!this.data || this.data.length === 0) return { categories: [], values: [] }

      const monthNames = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']

      const sortedData = [...this.data].sort((a, b) => {
        const dateA = this.parseDate(a.date || a.month || a.ocorrido_em || a.vencimento_em)
        const dateB = this.parseDate(b.date || b.month || b.ocorrido_em || b.vencimento_em)
        return dateA - dateB
      })

      const categories = []
      const values = []

      sortedData.forEach(item => {
        const dateStr = item.date || item.month || item.ocorrido_em || item.vencimento_em
        const date = this.parseDate(dateStr)

        if (isNaN(date.getTime())) {
          categories.push(dateStr || 'N/A')
        } else {
          const formattedDate = `${monthNames[date.getMonth()]}/${date.getFullYear()}`
          categories.push(formattedDate)
        }

        if (this.sortBy === 'value') {
          values.push(item.sum / 100)
        } else {
          values.push(item.count)
        }
      })

      return { categories, values }
    },
    parseDate(dateStr) {
      if (!dateStr) return new Date(NaN)

      // Tenta formato "YYYY-MM" (ex: "2024-01")
      if (/^\d{4}-\d{2}$/.test(dateStr)) {
        const [year, month] = dateStr.split('-')
        return new Date(parseInt(year), parseInt(month) - 1, 1)
      }

      // Tenta formato ISO ou outros
      return new Date(dateStr)
    },
    createChart() {
      if (this.chartInstance) {
        this.chartInstance.destroy()
        this.chartInstance = null
      }

      const theme = this.chartTheme
      const { categories, values } = this.buildSeries()

      const chartConfig = {
        chart: {
          backgroundColor: theme.backgroundColor,
          type: this.chartType,
          height: 400,
          style: { fontFamily: '"Montserrat", sans-serif' }
        },
        colors: theme.colors,
        credits: {
          enabled: false
        },
        title: {
          text: this.currentTitle,
          align: 'center',
          style: {
            color: theme.textColor,
            fontSize: '18px',
            fontWeight: '600'
          }
        },
        xAxis: {
          categories: categories,
          labels: {
            rotation: -45,
            style: {
              fontSize: '10px',
              color: theme.textColor
            }
          },
          gridLineColor: theme.gridLineColor
        },
        yAxis: {
          title: {
            text: this.sortBy === 'value' ? 'Valor Total (R$)' : 'Quantidade',
            style: { color: theme.textColor }
          },
          labels: {
            style: { color: theme.textColor }
          },
          gridLineColor: theme.gridLineColor
        },
        tooltip: {
          backgroundColor: theme.backgroundColor,
          borderColor: theme.gridLineColor,
          style: { color: theme.textColor },
          shared: true,
          crosshairs: true,
          valuePrefix: this.sortBy === 'value' ? 'R$ ' : '',
          valueDecimals: this.sortBy === 'value' ? 2 : 0
        },
        plotOptions: {
          line: {
            dataLabels: {
              enabled: false
            },
            enableMouseTracking: true,
            marker: {
              enabled: true,
              radius: 4
            }
          },
          area: {
            fillColor: {
              linearGradient: {
                x1: 0,
                y1: 0,
                x2: 0,
                y2: 1
              },
              stops: [
                [0, theme.colors[0]],
                [1, Highcharts.color(theme.colors[0]).setOpacity(0.1).get('rgba')]
              ]
            },
            marker: {
              enabled: true,
              radius: 3
            },
            lineWidth: 2,
            states: {
              hover: {
                lineWidth: 2
              }
            },
            threshold: null
          },
          column: {
            borderRadius: 6,
            borderWidth: 0
          }
        },
        legend: {
          enabled: false
        },
        series: [{
          type: this.chartType,
          name: this.sortBy === 'value' ? 'Valor' : 'Quantidade',
          data: values,
          color: theme.colors[0]
        }]
      }

      this.chartInstance = Highcharts.chart(this.$refs.targetChart, chartConfig)
    }
  },
  mixins: [ChartMix, ChartThemeMixin],
  mounted() {
    this.createChart()
  },
  beforeDestroy() {
    if (this.chartInstance) {
      this.chartInstance.destroy()
      this.chartInstance = null
    }
  },
  watch: {
    isDarkTheme: {
      handler() {
        this.$nextTick(() => {
          this.createChart()
        })
      },
      immediate: false
    },
    data: {
      handler() {
        this.$nextTick(() => {
          this.createChart()
        })
      },
      deep: true
    }
  },
  name: 'CmEmbarcadorTimelineChart'
}
</script>

<style scoped>
.chart-container {
  position: relative;
  width: 100%;
  border-radius: 12px;
  overflow: visible;
  background-color: var(--white-bg);
  padding: 15px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24);
}

.chart-header {
  position: absolute;
  top: 15px;
  right: 20px;
  z-index: 10;
  display: flex;
  gap: 8px;
}

/* CSS específico para o dropdown dos gráficos */
.chart-header .bkps-dropdown {
  background: var(--white-bg) !important;
  border: 1px solid var(--border-color) !important;
  color: var(--primary-text-color) !important;
}

.chart-header .bkps-dropdown-content {
  background-color: var(--white-bg) !important;
  color: var(--primary-text-color) !important;
  border: 1px solid var(--border-color) !important;
}

.chart-header .bkps-dropdown-item {
  color: var(--primary-text-color) !important;
}

.chart-header .bkps-dropdown-item:hover {
  background-color: var(--btn-hover-bg) !important;
}

.chart-header .hide {
  max-height: 0 !important;
  padding: 0 !important;
  border: none !important;
  box-shadow: none !important;
  margin: 0 !important;
}
</style>
